import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-4 py-12 text-gray-800 dark:text-gray-100">
      <h1 className="text-4xl font-bold mb-3 text-center">Privacy Policy</h1>
      <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-10">
        Last updated: January 2026
      </p>

      <section className="space-y-10 max-w-4xl mx-auto">
        {/* ================= ENGLISH ================= */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">English</h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg">1. Introduction</h3>
              <p>
                PTfinderNow ("we", "our", "us") respects your privacy and is
                committed to protecting your personal data. This Privacy Policy
                explains how we collect, use, store, and disclose information
                when you use our website, platform, and related services
                (collectively, the "Platform").
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">
                2. Who This Policy Applies To
              </h3>
              <p>
                This Privacy Policy applies to all users of the Platform,
                including visitors, clients, and experts/coaches ("Experts").
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">3. Information We Collect</h3>
              <p>
                We collect information you provide directly, including profile
                details, contact information, availability, pricing, gallery
                media, reviews, and subscription data. We may also collect
                technical data such as IP address, browser type, device
                information, and time zone for security and analytics purposes.
              </p>
              <p className="mt-2">
                If you contact us, we may collect the content of your messages
                and any information you choose to share.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">4. Cookies & Analytics</h3>
              <p>
                We may use cookies or similar technologies to enable core
                functionality (such as security and session management) and to
                understand usage of the Platform. You can control cookies
                through your browser settings. Disabling cookies may affect
                certain features.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">5. Public Information</h3>
              <p>
                PTfinderNow is a public marketplace. By registering as an
                Expert, you agree that your profile information may be publicly
                visible. This may include your name, profile photo, description,
                category, speciality, location, pricing, availability slots,
                gallery, reviews, and contact details (email, phone, WhatsApp),
                depending on platform settings.
              </p>
              <p className="mt-2">
                Please avoid posting sensitive personal data on your public
                profile.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">6. Contact Details</h3>
              <p>
                Experts voluntarily provide their contact details. PTfinderNow
                is not responsible for communications or interactions that occur
                once this information is displayed or shared on the Platform.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">7. Expert Responsibility</h3>
              <p>
                Experts are fully responsible for the accuracy of their
                information and content. PTfinderNow does not verify Expert
                qualifications, certifications, licenses, or background unless
                explicitly stated.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">8. Payments</h3>
              <p>
                PTfinderNow does not store full credit/debit card details.
                Payments, subscriptions, and Thanks Gifts (if enabled) may be
                processed by third-party payment providers such as Stripe.
                These providers handle payment information according to their
                own privacy policies.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">9. How We Use Information</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Provide, operate, and maintain the Platform</li>
                <li>Create and manage accounts and profiles</li>
                <li>Enable bookings, availability features, and communications</li>
                <li>Process subscriptions and platform-related transactions</li>
                <li>Improve security, prevent fraud, and enforce our Terms</li>
                <li>Analyze usage to improve features and performance</li>
                <li>Send important service messages (e.g., policy updates)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg">
                9A. Marketing & Promotional Use of Content
              </h3>
              <p>
                By uploading or providing any content to PTfinderNow (including
                profile photos, gallery images, videos, logos, and descriptions),
                Experts acknowledge and agree that PTfinderNow may use such
                content for platform operation, display, promotion, and
                marketing purposes.
              </p>
              <p className="mt-2">
                This may include use on PTfinderNow’s website, social media
                channels, promotional posts, advertisements, and marketing
                materials, only in connection with promoting the Platform and
                Expert profiles.
              </p>
              <p className="mt-2">
                Experts confirm that they own or have all necessary rights and
                permissions to upload such content. PTfinderNow does not sell
                Expert content as standalone media.
              </p>
              <p className="mt-2">
                Experts may request to stop future promotional use of their
                content by contacting{" "}
                <a
                  href="mailto:info@ptfindernow.com"
                  className="text-blue-500 hover:underline"
                >
                  info@ptfindernow.com
                </a>
                . Reasonable efforts will be made to honor such requests where
                feasible.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">10. Data Sharing</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>
                  With service providers who help us operate the Platform
                  (hosting, analytics, email delivery, payment providers)
                </li>
                <li>To comply with legal obligations</li>
                <li>To protect platform and user safety</li>
                <li>Business transfers where permitted by law</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg">
                11. International Data Transfers
              </h3>
              <p>
                Data may be processed in different countries. We take reasonable
                steps to ensure appropriate protections.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">12. Data Retention</h3>
              <p>
                We retain personal data only as long as necessary for legal,
                security, and operational purposes.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">13. Security</h3>
              <p>
                We use reasonable measures to protect data, but no system is
                100% secure.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">14. Children’s Privacy</h3>
              <p>The Platform is not intended for children.</p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">15. User Rights</h3>
              <p>
                You may request access, correction, or deletion of your data by
                contacting{" "}
                <a
                  href="mailto:info@ptfindernow.com"
                  className="text-blue-500 hover:underline"
                >
                  info@ptfindernow.com
                </a>
                .
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">16. Technical Issues</h3>
              <p>No compensation is provided for downtime or errors.</p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">17. Changes</h3>
              <p>Continued use means acceptance of updates.</p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">18. Contact Us</h3>
              <p>
                Contact{" "}
                <a
                  href="mailto:info@ptfindernow.com"
                  className="text-blue-500 hover:underline"
                >
                  info@ptfindernow.com
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* ================= ARABIC (FULL MATCH) ================= */}
        <div dir="rtl">
          <h2 className="text-2xl font-semibold mb-4">العربية</h2>

          <div className="space-y-6 text-right">
            <div>
              <h3 className="font-semibold text-lg">1. المقدمة</h3>
              <p>
                تحترم منصة PTfinderNow ("نحن" أو "خاصتنا" أو "لنا") خصوصيتك
                وتلتزم بحماية بياناتك الشخصية. توضح سياسة الخصوصية هذه كيفية
                جمع المعلومات واستخدامها وتخزينها والإفصاح عنها عند استخدامك
                لموقعنا الإلكتروني أو منصتنا أو خدماتنا ذات الصلة (ويشار إليها
                مجتمعة باسم "المنصة").
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">2. من تشمل هذه السياسة</h3>
              <p>
                تنطبق هذه السياسة على جميع مستخدمي المنصة، بما في ذلك الزوار
                والعملاء والخبراء/المدربين ("الخبراء").
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">3. المعلومات التي نجمعها</h3>
              <p>
                نجمع المعلومات التي تقدمها بشكل مباشر، بما في ذلك بيانات الملف
                الشخصي، ومعلومات التواصل، والتوفر، والأسعار، ووسائط المعرض
                (صور/فيديو)، والتقييمات، وبيانات الاشتراك. وقد نجمع أيضًا بيانات
                تقنية مثل عنوان IP ونوع المتصفح ومعلومات الجهاز والمنطقة الزمنية
                لأغراض الأمان والتحليلات.
              </p>
              <p className="mt-2">
                إذا تواصلت معنا، فقد نجمع محتوى رسائلك وأي معلومات تختار مشاركتها.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">4. ملفات تعريف الارتباط والتحليلات</h3>
              <p>
                قد نستخدم ملفات تعريف الارتباط (Cookies) أو تقنيات مشابهة لتمكين
                الوظائف الأساسية (مثل الأمان وإدارة الجلسات) وفهم كيفية استخدام
                المنصة. يمكنك التحكم بملفات تعريف الارتباط من خلال إعدادات
                المتصفح. قد يؤثر تعطيل ملفات تعريف الارتباط على بعض ميزات المنصة.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">5. المعلومات العامة</h3>
              <p>
                PTfinderNow هي سوق عامة. عند تسجيلك كخبير، فإنك توافق على أن تكون
                معلومات ملفك الشخصي ظاهرة للعامة. وقد يشمل ذلك الاسم، وصورة الملف،
                والوصف، والفئة/التخصص، والموقع، والأسعار، ومواعيد التوفر، والمعرض،
                والتقييمات، وبيانات التواصل (البريد الإلكتروني، الهاتف، واتساب)
                بحسب إعدادات المنصة.
              </p>
              <p className="mt-2">
                يرجى تجنب نشر بيانات شخصية حساسة في ملفك العام.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">6. بيانات التواصل</h3>
              <p>
                يقوم الخبراء بإدخال بيانات التواصل بإرادتهم. لا تتحمل PTfinderNow
                أي مسؤولية عن أي تواصل أو تفاعلات تحدث بعد عرض هذه المعلومات أو
                مشاركتها عبر المنصة.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">7. مسؤولية الخبراء</h3>
              <p>
                الخبراء مسؤولون بالكامل عن دقة معلوماتهم ومحتواهم. لا تقوم
                PTfinderNow بالتحقق من مؤهلات الخبراء أو شهاداتهم أو تراخيصهم أو
                خلفياتهم إلا إذا تم التصريح بذلك بشكل صريح.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">8. المدفوعات</h3>
              <p>
                لا تقوم PTfinderNow بتخزين بيانات بطاقات الائتمان/الخصم كاملة.
                قد تتم معالجة المدفوعات والاشتراكات و"هدايا الشكر" (إن تم تفعيلها)
                عبر مزودي خدمات دفع خارجيين مثل Stripe. ويتعامل هؤلاء المزودون مع
                معلومات الدفع وفقًا لسياسات الخصوصية الخاصة بهم.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">9. كيف نستخدم المعلومات</h3>
              <ul className="list-disc pr-6 mt-2">
                <li>تقديم وتشغيل وصيانة المنصة</li>
                <li>إنشاء وإدارة الحسابات والملفات الشخصية</li>
                <li>تمكين الحجوزات وميزات التوفر ووسائل التواصل</li>
                <li>معالجة الاشتراكات والمعاملات المتعلقة بالمنصة</li>
                <li>تعزيز الأمان ومنع الاحتيال وتطبيق الشروط</li>
                <li>تحليل الاستخدام لتحسين الميزات والأداء</li>
                <li>إرسال رسائل خدمة مهمة (مثل تحديثات السياسات)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg">9A. استخدام المحتوى لأغراض التسويق والترويج</h3>
              <p>
                عند رفع أو تقديم أي محتوى إلى PTfinderNow (بما في ذلك صور الملف
                الشخصي، صور/فيديو المعرض، الشعارات، والأوصاف)، يقر الخبراء ويوافقون
                على أن PTfinderNow قد تستخدم هذا المحتوى لأغراض تشغيل المنصة
                وعرضه والترويج والتسويق.
              </p>
              <p className="mt-2">
                قد يشمل ذلك استخدامه على موقع PTfinderNow ووسائل التواصل الاجتماعي
                والمنشورات الترويجية والإعلانات والمواد التسويقية، وذلك فقط
                فيما يتعلق بالترويج للمنصة وملفات الخبراء.
              </p>
              <p className="mt-2">
                يقر الخبراء بأنهم يملكون أو لديهم جميع الحقوق والأذونات اللازمة
                لرفع هذا المحتوى. لا تقوم PTfinderNow ببيع محتوى الخبراء كمواد
                مستقلة.
              </p>
              <p className="mt-2">
                يمكن للخبراء طلب إيقاف الاستخدام الترويجي المستقبلي لمحتواهم عبر
                التواصل مع{" "}
                <a
                  href="mailto:info@ptfindernow.com"
                  className="text-blue-500 hover:underline"
                >
                  info@ptfindernow.com
                </a>
                . وسنبذل جهودًا معقولة لتنفيذ هذه الطلبات متى كان ذلك ممكنًا.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">10. مشاركة البيانات</h3>
              <ul className="list-disc pr-6 mt-2">
                <li>
                  مع مزودي الخدمات الذين يساعدوننا على تشغيل المنصة (مثل الاستضافة
                  والتحليلات وإرسال البريد ومزودي الدفع)
                </li>
                <li>للامتثال للالتزامات القانونية</li>
                <li>لحماية سلامة المنصة والمستخدمين</li>
                <li>في حال نقل/بيع/اندماج أعمال حيث يسمح القانون بذلك</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg">11. نقل البيانات دوليًا</h3>
              <p>
                قد تتم معالجة البيانات في دول مختلفة. نتخذ خطوات معقولة لضمان وجود
                حماية مناسبة.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">12. الاحتفاظ بالبيانات</h3>
              <p>
                نحتفظ بالبيانات الشخصية فقط للمدة اللازمة للأغراض القانونية
                والأمنية والتشغيلية.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">13. الأمان</h3>
              <p>
                نستخدم إجراءات معقولة لحماية البيانات، ولكن لا يوجد نظام آمن بنسبة
                100%.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">14. خصوصية الأطفال</h3>
              <p>المنصة غير مخصصة للأطفال.</p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">15. حقوق المستخدم</h3>
              <p>
                يمكنك طلب الوصول إلى بياناتك أو تصحيحها أو حذفها عبر التواصل مع{" "}
                <a
                  href="mailto:info@ptfindernow.com"
                  className="text-blue-500 hover:underline"
                >
                  info@ptfindernow.com
                </a>
                .
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">16. الأعطال التقنية</h3>
              <p>لا يتم تقديم أي تعويض عن توقف الخدمة أو الأخطاء التقنية.</p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">17. التعديلات</h3>
              <p>استمرار استخدامك للمنصة يعني موافقتك على أي تحديثات.</p>
            </div>

            <div>
              <h3 className="font-semibold text-lg">18. تواصل معنا</h3>
              <p>
                للتواصل:{" "}
                <a
                  href="mailto:info@ptfindernow.com"
                  className="text-blue-500 hover:underline"
                >
                  info@ptfindernow.com
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center">
            Contact us at{" "}
            <a
              href="mailto:info@ptfindernow.com"
              className="text-blue-500 hover:underline"
            >
              info@ptfindernow.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
